// Dependency-free date formatting for A6 tables (spec §6.2): "Jul 17" / "6 days ago",
// never "7/17/2026". `now` is injectable so tests stay deterministic.
const MS_PER_DAY = 86_400_000

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value)
}

export function formatShortDate(value: string | Date, now: Date = new Date()): string {
  const d = toDate(value)
  // Compare/format in UTC: callers commonly pass date-only values serialized as UTC
  // midnight (e.g. "2026-07-17T00:00:00Z"). Formatting in the browser's local zone
  // would shift those back a calendar day for any timezone behind UTC.
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear()
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d)
}

export function formatRelativeDate(value: string | Date, now: Date = new Date()): string {
  const d = toDate(value)
  // The value is a calendar date encoded at UTC midnight (see formatShortDate), so read
  // its day with UTC accessors. `now`, however, is a real instant — bucket it by the
  // viewer's LOCAL calendar day, or an evening "today" in a behind-UTC zone (all US
  // timezones) would read "Yesterday" for the last few hours of every day.
  const valueDay = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  const nowDay = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((nowDay - valueDay) / MS_PER_DAY)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days <= 30) return `${days} days ago`
  return formatShortDate(d, now)
}
